import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Instagram, ArrowLeft, Loader2, Tag, X, CreditCard, Banknote, Gift } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { formatPrice } from '../lib/format';
import { isSaleActive, effectivePrice } from '../lib/sale';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { t, getLocalizedText, language } = useLanguage();
  const { cartItems, getCartTotal, clearCart } = useCart();
  const { customer, authHeader } = useCustomerAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    delivery: 'pickup',
    payment_method: 'cash_on_delivery',
    address: '',
    city: '',
    zip: '',
    country: '',
    notes: '',
  });

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoNotice, setPromoNotice] = useState('');
  const [discount, setDiscount] = useState(0);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const subtotal = getCartTotal();
  const saleSubtotal = cartItems.reduce((s, item) => s + (isSaleActive(item) ? item.salePrice * item.quantity : 0), 0);
  const hasHookahOrSet = cartItems.some(item => item.category === 'hookah' || item.category === 'set');

  // Loyalty discount (for logged-in customers)
  const [loyalty, setLoyalty] = useState(null);
  useEffect(() => {
    if (!customer) { setLoyalty(null); return; }
    let active = true;
    axios.post(`${API}/customer/loyalty`, { subtotal, sale_subtotal: saleSubtotal }, { headers: authHeader() })
      .then((r) => { if (active) setLoyalty(r.data); })
      .catch(() => { if (active) setLoyalty(null); });
    return () => { active = false; };
  }, [customer, subtotal, saleSubtotal, authHeader]);

  const promoDiscount = discount;
  const loyaltyDiscount = loyalty && loyalty.available ? loyalty.calculated_discount : 0;
  // Loyalty is NOT combinable with a promo code -> the larger discount wins.
  let finalDiscount = 0;
  let discountSource = null;
  if (loyaltyDiscount > 0 && promoDiscount > 0) {
    if (loyaltyDiscount >= promoDiscount) { finalDiscount = loyaltyDiscount; discountSource = 'loyalty'; }
    else { finalDiscount = promoDiscount; discountSource = 'promo'; }
  } else if (loyaltyDiscount > 0) { finalDiscount = loyaltyDiscount; discountSource = 'loyalty'; }
  else if (promoDiscount > 0) { finalDiscount = promoDiscount; discountSource = 'promo'; }
  const bothAvailable = loyaltyDiscount > 0 && promoDiscount > 0;
  const total = subtotal - finalDiscount;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDeliveryChange = (value) => {
    setFormData(prev => ({ ...prev, delivery: value }));
  };

  const handlePaymentChange = (value) => {
    setFormData(prev => ({ ...prev, payment_method: value }));
  };

  // Apply promo code
  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    
    setPromoValidating(true);
    setPromoError('');
    setPromoNotice('');
    
    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode.trim(),
        total: subtotal,
        sale_subtotal: saleSubtotal,
      });
      
      setPromoApplied(response.data);
      setDiscount(response.data.calculated_discount);
      if (response.data.applied_to_non_sale_only) {
        setPromoNotice(language === 'hu'
          ? 'A promóciós kód nem vonható össze az akciós termékekkel, ezért csak a többi (nem akciós) termékre lett alkalmazva.'
          : 'The promo code cannot be combined with sale items, so it was applied only to the non-sale products.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      let message;
      if (typeof detail === 'string' && detail.startsWith('MIN_ORDER:')) {
        const amount = parseInt(detail.split(':')[1], 10);
        message = language === 'hu'
          ? `Ez a promóciós kód csak ${formatPrice(amount)} Ft feletti rendelésnél használható.`
          : `This promo code can only be used for orders above ${formatPrice(amount)} HUF.`;
      } else if (detail === 'PROMO_FIXED_SALE_CONFLICT') {
        message = language === 'hu'
          ? 'Ezt a promóciós kódot nem tudja összevonni ezzel a rendeléssel, mivel akciós termék van a kosarában.'
          : 'This promo code cannot be combined with this order because there is a sale item in your cart.';
      } else if (detail === 'Promo code has expired') {
        message = language === 'hu' ? 'A promóciós kód lejárt.' : 'The promo code has expired.';
      } else if (detail === 'Promo code not found') {
        message = language === 'hu' ? 'Érvénytelen promóciós kód.' : 'Invalid promo code.';
      } else if (detail === 'Promo code usage limit reached') {
        message = language === 'hu' ? 'A promóciós kód elérte a felhasználási limitet.' : 'Promo code usage limit reached.';
      } else if (detail === 'Promo code is not active') {
        message = language === 'hu' ? 'A promóciós kód nem aktív.' : 'Promo code is not active.';
      } else {
        message = detail || t.checkout.promoCodeInvalid;
      }
      setPromoError(message);
      setPromoApplied(null);
      setDiscount(0);
    } finally {
      setPromoValidating(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoApplied(null);
    setPromoError('');
    setPromoNotice('');
    setDiscount(0);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Required';
    if (!formData.email.trim()) newErrors.email = 'Required';
    if (!formData.phone.trim()) newErrors.phone = 'Required';
    
    if (formData.delivery === 'domestic') {
      if (!formData.address.trim()) newErrors.address = 'Required';
      if (!formData.city.trim()) newErrors.city = 'Required';
      if (!formData.zip.trim()) newErrors.zip = 'Required';
    }
    
    if (formData.delivery === 'international') {
      if (!formData.phone.trim()) newErrors.phone = 'Required';
      if (!formData.country.trim()) newErrors.country = 'Required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      const orderData = {
        ...formData,
        promo_code: discountSource === 'promo' ? (promoApplied?.code || null) : null,
        items: cartItems.map(item => ({
          product_id: item.id,
          name: getLocalizedText(item.name),
          price: effectivePrice(item),
          quantity: item.quantity,
          image: item.image,
        })),
        subtotal: subtotal,
        discount: finalDiscount,
        total: total,
        sale_subtotal: saleSubtotal,
        discount_source: discountSource,
      };

      await axios.post(`${API}/orders`, orderData, { headers: authHeader() });
      
      setIsSubmitted(true);
      clearCart();
    } catch (error) {
      console.error('Error submitting order:', error);
      setIsSubmitted(true);
      clearCart();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cartItems.length === 0 && !isSubmitted) {
    navigate('/cart');
    return null;
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black py-20 px-4">
        <div className="container mx-auto max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center mx-auto mb-6">
            <Check className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">
            {t.checkout.success}
          </h1>
          {formData.delivery === 'international' && (
            <p className="text-zinc-400 mb-4">
              {t.checkout.internationalNote}
            </p>
          )}
          {formData.payment_method === 'prepayment' && (
            <p className="text-zinc-400 mb-4">
              {t.checkout.prepaymentNote}
            </p>
          )}
          <div className="space-y-4">
            <Button
              size="lg"
              className="bg-white text-black hover:bg-zinc-200"
              onClick={() => navigate('/')}
            >
              {t.nav.home}
            </Button>
            <div>
              <a
                href="https://www.instagram.com/_shishavibes._/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
              >
                <Instagram className="h-5 w-5" />
                @_shishavibes._
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-8 px-4">
      <div className="container mx-auto max-w-4xl">
        <Button
          variant="ghost"
          className="text-zinc-400 hover:text-white mb-6"
          onClick={() => navigate('/cart')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t.common.back}
        </Button>

        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">{t.checkout.title}</h1>

        {hasHookahOrSet && (
          <div className="mb-8 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-4" data-testid="checkout-hookah-warning">
            {language === 'hu'
              ? 'Figyelem! A kosár vízipipát/teljes készletes vízipipát tartalmaz, ezért a vízipipa várható szállítási ideje 5-7 munkanap, a kiegészítőket igény esetén azonnal postázni tudjuk.'
              : 'Attention! Your cart contains a hookah/complete hookah set, so the expected delivery time for the hookah is 5-7 business days; we can ship the accessories immediately upon request.'}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
            {/* Contact Info */}
            <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">
                {language === 'hu' ? 'Kapcsolati adatok' : 'Contact Information'}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="name" className="text-zinc-300">{t.checkout.name}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.name ? 'border-red-500' : ''}`}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-zinc-300">{t.checkout.email}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-zinc-300">{t.checkout.phone}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="+36 XX XXX XXXX"
                  />
                </div>
              </div>
            </div>

            {/* Delivery Method */}
            <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">{t.checkout.delivery}</h2>
              
              <RadioGroup value={formData.delivery} onValueChange={handleDeliveryChange}>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                    <RadioGroupItem value="pickup" id="pickup" className="border-zinc-600 text-white" />
                    <Label htmlFor="pickup" className="text-white cursor-pointer flex-1">
                      <span className="font-medium text-sm md:text-base">{t.checkout.pickup}</span>
                      <span className="block text-xs md:text-sm text-zinc-500">Debrecen, Hungary</span>
                    </Label>
                    <span className="text-green-500 font-medium text-sm">FREE</span>
                  </div>

                  <div className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                    <RadioGroupItem value="domestic" id="domestic" className="border-zinc-600 text-white" />
                    <Label htmlFor="domestic" className="text-white cursor-pointer flex-1">
                      <span className="font-medium text-sm md:text-base">{t.checkout.domestic}</span>
                      <span className="block text-xs md:text-sm text-zinc-500">1-3 {language === 'hu' ? 'munkanap' : 'business days'}</span>
                    </Label>
                    <span className="text-right text-xs md:text-sm text-amber-400 font-medium leading-tight" data-testid="domestic-shipping-fee">
                      {language === 'hu' ? 'GLS csomagszállító' : 'GLS courier'}
                      <span className="block text-zinc-500">{language === 'hu' ? 'díjszabása szerint' : 'per their rates'}</span>
                    </span>
                  </div>

                  <div className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                    <RadioGroupItem value="international" id="international" className="border-zinc-600 text-white" />
                    <Label htmlFor="international" className="text-white cursor-pointer flex-1">
                      <span className="font-medium text-sm md:text-base">{t.checkout.international}</span>
                      <span className="block text-xs md:text-sm text-zinc-500">{t.checkout.internationalNote}</span>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Address (for domestic delivery) */}
            {formData.delivery === 'domestic' && (
              <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">{t.checkout.address}</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="address" className="text-zinc-300">{t.checkout.address}</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.address ? 'border-red-500' : ''}`}
                      placeholder="Street address"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city" className="text-zinc-300">{t.checkout.city}</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.city ? 'border-red-500' : ''}`}
                      placeholder="Budapest"
                    />
                  </div>

                  <div>
                    <Label htmlFor="zip" className="text-zinc-300">{t.checkout.zip}</Label>
                    <Input
                      id="zip"
                      name="zip"
                      value={formData.zip}
                      onChange={handleChange}
                      className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.zip ? 'border-red-500' : ''}`}
                      placeholder="1000"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Country (for international) */}
            {formData.delivery === 'international' && (
              <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
                <h2 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">{t.checkout.country}</h2>
                <div>
                  <Label htmlFor="country" className="text-zinc-300">{t.checkout.country}</Label>
                  <Input
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={`mt-1 bg-zinc-800 border-zinc-700 text-white ${errors.country ? 'border-red-500' : ''}`}
                    placeholder="Country name"
                  />
                </div>
                <p className="mt-4 text-sm text-zinc-500">
                  {t.checkout.internationalNote}
                </p>
              </div>
            )}

            {/* Payment Method */}
            <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-white mb-4 md:mb-6">{t.checkout.paymentMethod}</h2>
              
              <RadioGroup value={formData.payment_method} onValueChange={handlePaymentChange}>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                    <RadioGroupItem value="cash_on_delivery" id="cod" className="border-zinc-600 text-white" />
                    <Banknote className="h-5 w-5 text-zinc-400" />
                    <Label htmlFor="cod" className="text-white cursor-pointer flex-1">
                      <span className="font-medium text-sm md:text-base">{t.checkout.cashOnDelivery}</span>
                    </Label>
                  </div>

                  <div className="flex items-center space-x-3 p-3 md:p-4 rounded-lg border border-zinc-800 hover:border-zinc-600 cursor-pointer">
                    <RadioGroupItem value="prepayment" id="prepay" className="border-zinc-600 text-white" />
                    <CreditCard className="h-5 w-5 text-zinc-400" />
                    <Label htmlFor="prepay" className="text-white cursor-pointer flex-1">
                      <span className="font-medium text-sm md:text-base">{t.checkout.prepayment}</span>
                      <span className="block text-xs md:text-sm text-zinc-500">{t.checkout.prepaymentNote}</span>
                    </Label>
                  </div>
                </div>
              </RadioGroup>
            </div>

            {/* Notes */}
            <div className="bg-zinc-900 rounded-lg p-4 md:p-6">
              <Label htmlFor="notes" className="text-zinc-300">{t.checkout.notes}</Label>
              <Textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="mt-1 bg-zinc-800 border-zinc-700 text-white min-h-[100px]"
                placeholder="Any special requests..."
              />
            </div>

            {/* Submit Button (mobile) */}
            <div className="lg:hidden">
              <Button
                type="submit"
                size="lg"
                className="w-full bg-white text-black hover:bg-zinc-200 font-semibold py-6"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ...
                  </>
                ) : (
                  t.checkout.submit
                )}
              </Button>
            </div>
          </form>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-zinc-900 rounded-lg p-4 md:p-6 sticky top-24">
              <h2 className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6">{t.cart.title}</h2>
              
              <div className="space-y-3 mb-4 md:mb-6 max-h-48 md:max-h-60 overflow-y-auto">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-14 h-14 md:w-16 md:h-16 bg-zinc-800 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={item.image}
                        alt={getLocalizedText(item.name)}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm text-white truncate">{getLocalizedText(item.name)}</p>
                      <p className="text-xs text-zinc-500">x{item.quantity}</p>
                      <p className={`text-xs md:text-sm font-semibold ${isSaleActive(item) ? 'text-red-500' : 'text-white'}`}>
                        {formatPrice(effectivePrice(item) * item.quantity)} HUF
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Loyalty banner (logged-in customers) */}
              {customer && loyalty && (loyalty.available || (loyalty.potential_percent > 0)) && (
                <div className="border-t border-zinc-800 pt-4 mb-2">
                  <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="loyalty-checkout-banner">
                    <Gift className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-300 text-xs">
                      {loyalty.available
                        ? (language === 'hu'
                            ? `Hűségkedvezmény alkalmazva: ${loyalty.percent}% (-${formatPrice(loyalty.calculated_discount)} Ft) a nem akciós termékekre.`
                            : `Loyalty discount applied: ${loyalty.percent}% (-${formatPrice(loyalty.calculated_discount)} HUF) on non-sale items.`)
                        : (language === 'hu'
                            ? `${loyalty.potential_percent}% hűségkedvezmény érhető el, ha a nem akciós termékek értéke eléri a ${formatPrice(loyalty.potential_min_order)} Ft-ot.`
                            : `A ${loyalty.potential_percent}% loyalty discount is available if your non-sale items reach ${formatPrice(loyalty.potential_min_order)} HUF.`)}
                    </p>
                  </div>
                </div>
              )}

              {/* Promo Code */}
              <div className="border-t border-zinc-800 pt-4 mb-4">
                <Label className="text-zinc-300 text-sm">{t.checkout.promoCode}</Label>
                {promoApplied ? (
                  <div className="mt-2 flex items-center justify-between p-3 bg-green-900/30 border border-green-700 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-green-500" />
                      <span className="text-green-400 font-medium">{promoApplied.code}</span>
                      <span className="text-green-500 text-sm">
                        (-{promoApplied.discount_type === 'percentage' 
                          ? `${promoApplied.discount_value}%` 
                          : `${formatPrice(promoApplied.discount_value)} HUF`})
                      </span>
                    </div>
                    <button 
                      onClick={handleRemovePromo}
                      className="text-zinc-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder={t.checkout.promoCodePlaceholder}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-zinc-700 text-white hover:bg-zinc-800 px-3 md:px-4"
                      onClick={handleApplyPromo}
                      disabled={promoValidating || !promoCode.trim()}
                    >
                      {promoValidating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        t.checkout.promoCodeApply
                      )}
                    </Button>
                  </div>
                )}
                {promoError && (
                  <p className="text-red-500 text-sm mt-2">{promoError}</p>
                )}
                {promoNotice && (
                  <p className="text-amber-400 text-sm mt-2" data-testid="promo-notice">{promoNotice}</p>
                )}
              </div>

              {/* Price Summary */}
              <div className="border-t border-zinc-800 pt-4 mb-4 md:mb-6 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">{t.checkout.subtotal}</span>
                  <span className="text-white">{formatPrice(subtotal)} HUF</span>
                </div>
                {finalDiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-500">
                      {discountSource === 'loyalty' ? (language === 'hu' ? 'Hűségkedvezmény' : 'Loyalty discount') : t.checkout.discount}
                      {discountSource === 'loyalty' && loyalty ? ` (${loyalty.percent}%)` : ''}
                    </span>
                    <span className="text-green-500">-{formatPrice(finalDiscount)} HUF</span>
                  </div>
                )}
                {bothAvailable && (
                  <p className="text-amber-400 text-xs" data-testid="discount-not-combinable-note">
                    {language === 'hu'
                      ? 'A hűségkedvezmény és a promóciós kód nem vonható össze — a nagyobb kedvezmény lett alkalmazva.'
                      : 'Loyalty discount and promo code cannot be combined — the larger discount was applied.'}
                  </p>
                )}
                <div className="flex justify-between pt-2 border-t border-zinc-800">
                  <span className="text-lg font-semibold text-white">{t.cart.total}</span>
                  <span className="text-lg font-bold text-white">
                    {formatPrice(total)} HUF
                  </span>
                </div>
              </div>

              {/* Submit Button (desktop) */}
              <div className="hidden lg:block">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-white text-black hover:bg-zinc-200 font-semibold"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      ...
                    </>
                  ) : (
                    t.checkout.submit
                  )}
                </Button>
              </div>

              {/* Instagram Contact */}
              <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-zinc-800">
                <p className="text-xs md:text-sm text-zinc-500 mb-2 md:mb-3">{t.footer.support}:</p>
                <a
                  href="https://www.instagram.com/_shishavibes._/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white hover:text-zinc-300 transition-colors text-sm"
                >
                  <Instagram className="h-4 w-4 md:h-5 md:w-5" />
                  @_shishavibes._
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
